"use client"

import clsx from 'clsx'
import { AnimatePresence, motion } from 'framer-motion'
import { useLocale, useTranslations } from 'next-intl'
import PropTypes from 'prop-types'
import { useMemo } from 'react'

import {
  usageIndicatorPropTypes,
  usageIndicatorDefaultProps,
  usageMetadataPropType,
} from './usage-indicator.types'

const SIZE_MAP = {
  sm: 72,
  md: 112,
  lg: 160,
}

const STROKE_WIDTH = 10

function getPercentUsed(usageMetadata) {
  const total = Math.max(usageMetadata.limit ?? 0, 0)

  if (!total) {
    return 0
  }

  const remaining = Math.max(usageMetadata.remaining ?? 0, 0)
  const used = Math.max(total - remaining, 0)

  return Math.min(Math.max((used / total) * 100, 0), 100)
}

function formatResetTime(resetAt, locale) {
  if (!resetAt) {
    return null
  }

  const resetDate = resetAt instanceof Date ? resetAt : new Date(resetAt)

  if (Number.isNaN(resetDate.getTime())) {
    return null
  }

  return new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    month: 'short',
    day: 'numeric',
  }).format(resetDate)
}

function UsageIndicator({ usageMetadata, size, showLegend, className, legendText: customLegendText }) {
  const locale = useLocale()
  const t = useTranslations('oracle.usageIndicator')
  const numberFormatter = useMemo(() => new Intl.NumberFormat(locale), [locale])

  if (!usageMetadata) {
    const zero = numberFormatter.format(0)
    const defaultLegend = t('legend', { used: zero, total: zero })
    const legendText = customLegendText || defaultLegend

    return (
      <section
        data-testid="usage-indicator"
        className={clsx(
          'relative flex flex-col items-center justify-center text-[#FBFBFE]',
          className,
        )}
        aria-label={t('ariaLabel')}
      >
        <span className="sr-only" aria-live="polite">
          {legendText}
        </span>
      </section>
    )
  }

  const diameter = SIZE_MAP[size] ?? SIZE_MAP.md
  const radius = diameter / 2 - STROKE_WIDTH
  const circumference = 2 * Math.PI * radius
  const percentUsed = getPercentUsed(usageMetadata)
  const dashOffset = circumference - (percentUsed / 100) * circumference
  const total = Math.max(usageMetadata.limit ?? 0, 0)
  const remaining = Math.max(usageMetadata.remaining ?? 0, 0)
  const usedCount = Math.max(total - remaining, 0)
  const formattedTotal = numberFormatter.format(total)
  const formattedUsed = numberFormatter.format(usedCount)
  const defaultLegend = t('legend', { used: formattedUsed, total: formattedTotal })
  const legendText = customLegendText || defaultLegend
  const resetTime = formatResetTime(usageMetadata.resetAt, locale)
  const resetLabel = resetTime ? t('reset', { time: resetTime }) : null

  return (
    <section
      data-testid="usage-indicator"
      className={clsx(
        'relative flex flex-col items-center justify-center text-[#FBFBFE]',
        className,
      )}
      aria-label={t('ariaLabel')}
    >
      <div className="flex flex-row items-center justify-center gap-2 mt-3">
  {Array.from({ length: total }).map((_, idx) => (
    <span
      key={idx}
      className={
        "text-2xl transition-all duration-300" +
        (idx < usedCount
          ? " grayscale opacity-40"
          : " drop-shadow-[0_0_7px_#a45de8aa]")
      }
      style={{
        filter: idx < usedCount
          ? "grayscale(80%) opacity(0.5)"
          : "drop-shadow(0 0 12px #a45de8)",
        transition: "all 0.3s"
      }}
      aria-label={
        idx < usedCount ? t('usedOrbLabel', { n: idx + 1 }) : t('availableOrbLabel', { n: idx + 1 })
      }
    >
      🔮
    </span>
  ))}
</div>

      <AnimatePresence>
        {showLegend ? (
          <motion.div
            key="legend"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.2 }}
            className="mt-3 text-center text-sm text-[#D4B6FF]"
          >
            <p>{legendText}</p>
            {resetLabel ? (
              <p className="text-xs text-[#D4B6FF]/70">{resetLabel}</p>
            ) : null}
          </motion.div>
        ) : null}
      </AnimatePresence>

      <span className="sr-only" aria-live="polite">
        {legendText}
      </span>
    </section>
  )
}

UsageIndicator.propTypes = {
  ...usageIndicatorPropTypes,
  usageMetadata: usageMetadataPropType,
}

UsageIndicator.defaultProps = usageIndicatorDefaultProps

export const UsageMetadataShape = usageMetadataPropType

export default UsageIndicator
